import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Upload, Trash2, Plus, Search, Pencil } from 'lucide-react';
import api, { errMsg } from '../lib/api';

export const inp = "w-full border hairline px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0B0F12]";
export const lbl = "block font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A8F95] mb-1.5";

export function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className={lbl}>{label}</label>
      {children}
    </div>
  );
}

export function Tgl({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer py-1">
      <input data-testid={testid} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#0B0F12]" />
      <span className="text-sm">{label}</span>
    </label>
  );
}

export function Sel({ label, value, onChange, options, testid }) {
  return (
    <Field label={label}>
      <select data-testid={testid} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inp}>
        <option value="">—</option>
        {options.map((o) => {
          const [v, l] = Array.isArray(o) ? o : [o, o];
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </Field>
  );
}

export function ImagePicker({ value, onChange, label = 'Image', testid }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd);
      onChange(data.url);
      toast.success('Uploaded');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Field label={label}>
      <div className="flex gap-2 items-start">
        {value && <img src={value} alt="" className="w-16 h-16 object-cover border hairline" />}
        <div className="flex-1 space-y-2">
          <input data-testid={testid} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload" className={inp} />
          <div className="flex gap-2">
            <button type="button" data-testid={`${testid}-upload`} onClick={() => ref.current?.click()} disabled={busy}
              className="border hairline px-3 py-2 font-mono2 text-[10px] uppercase tracking-widest hover:border-[#0B0F12] flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" /> {busy ? 'Uploading…' : 'Upload'}
            </button>
            {value && <button type="button" onClick={() => onChange('')} className="border hairline px-3 py-2 font-mono2 text-[10px] uppercase tracking-widest text-[#E54D2E]"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
      </div>
    </Field>
  );
}

export function FilePicker({ value, onChange, label = 'Document (PDF)', testid }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd);
      onChange(data.url);
      toast.success('Uploaded');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <input data-testid={testid} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="PDF URL or upload" className={inp} />
        <button type="button" data-testid={`${testid}-upload`} onClick={() => ref.current?.click()} disabled={busy}
          className="border hairline px-3 font-mono2 text-[10px] uppercase tracking-widest hover:border-[#0B0F12] flex items-center gap-1.5 whitespace-nowrap">
          <Upload className="w-3.5 h-3.5" /> {busy ? '…' : 'Upload'}
        </button>
      </div>
      <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </Field>
  );
}

export function Modal({ title, onClose, children, wide }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-modal">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-[#FAFAF8] w-full ${wide ? 'max-w-4xl' : 'max-w-xl'} my-8 border hairline shadow-2xl`}>
        <div className="flex items-center justify-between p-5 border-b hairline sticky top-0 bg-[#FAFAF8] z-10">
          <p className="font-display font-black uppercase text-xl">{title}</p>
          <button data-testid="modal-close" onClick={onClose} className="p-1.5"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function PageHead({ title, sub, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display font-black uppercase text-3xl tracking-tight">{title}</h1>
        {sub && <p className="text-sm text-[#575B60] mt-1">{sub}</p>}
      </div>
      <div className="flex gap-2">{children}</div>
    </div>
  );
}

const renderField = (f, form, setForm, ctx) => {
  const val = form[f.key] ?? '';
  const setv = (v) => setForm((p) => ({ ...p, [f.key]: v }));
  const tid = `field-${f.key}`;
  switch (f.type) {
    case 'textarea': return <Field key={f.key} label={f.label}><textarea data-testid={tid} rows={f.rows || 4} value={val} onChange={(e) => setv(e.target.value)} className={inp} /></Field>;
    case 'number': return <Field key={f.key} label={f.label}><input data-testid={tid} type="number" step="any" value={val} onChange={(e) => setv(e.target.value === '' ? '' : Number(e.target.value))} className={inp} /></Field>;
    case 'select': return <Sel key={f.key} label={f.label} value={val} onChange={setv} options={f.options} testid={tid} />;
    case 'toggle': return <Tgl key={f.key} label={f.label} checked={form[f.key]} onChange={setv} testid={tid} />;
    case 'image': return <ImagePicker key={f.key} label={f.label} value={val} onChange={setv} testid={tid} />;
    case 'file': return <FilePicker key={f.key} label={f.label} value={val} onChange={setv} testid={tid} />;
    case 'product': return <Sel key={f.key} label={f.label} value={val} onChange={(v) => { setv(v); const p = (ctx.products || []).find((x) => x.id === v); if (p && f.productNameKey) setForm((prev) => ({ ...prev, [f.productNameKey]: `${p.name} ${p.strength}` })); }} options={(ctx.products || []).map((p) => [p.id, `${p.name} ${p.strength} (${p.format})`])} testid={tid} />;
    default: return <Field key={f.key} label={f.label}><input data-testid={tid} value={val} onChange={(e) => setv(e.target.value)} className={inp} /></Field>;
  }
};

export function ResourceAdmin({ title, sub, endpoint, fields, columns, searchKeys = [], defaults = {}, afterSave, testid }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/${endpoint}`, { params: q ? { q } : {} });
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { load(); }, [q]);
  useEffect(() => {
    if (fields.some((f) => f.type === 'product')) {
      api.get('/admin/products').then((r) => setProducts(r.data)).catch(() => {});
    }
  }, []);

  const openNew = () => { setForm({ ...defaults }); setEditing({}); };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing?.id) await api.put(`/admin/${endpoint}/${editing.id}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      if (afterSave) await afterSave(payload);
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/${endpoint}/${item.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={testid}>
      <PageHead title={title} sub={sub}>
        <button data-testid={`${testid}-add`} onClick={openNew} className="bg-[#0B0F12] text-white px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-1.5 hover:bg-[#E54D2E] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </PageHead>
      {searchKeys.length > 0 && (
        <div className="flex items-center gap-2 border hairline bg-white px-3 py-2.5 mb-4 max-w-sm">
          <Search className="w-4 h-4 text-[#8A8F95]" />
          <input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="flex-1 text-sm focus:outline-none" />
        </div>
      )}
      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b hairline">
              {columns.map((c) => <th key={c.key} className="text-left p-3 font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95]">{c.label}</th>)}
              <th className="p-3 w-24" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b hairline last:border-0 hover:bg-[#FAFAF8]">
                {columns.map((c) => <td key={c.key} className="p-3 align-top">{c.render ? c.render(item) : String(item[c.key] ?? '—')}</td>)}
                <td className="p-3">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`${testid}-edit-${item.id}`} onClick={() => openEdit(item)} className="p-1.5 hover:bg-[#F4F4F0]"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`${testid}-delete-${item.id}`} onClick={() => remove(item)} className="p-1.5 hover:bg-[#F4F4F0] text-[#E54D2E]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="p-8 text-center text-[#8A8F95] text-sm">No entries yet</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.id ? `Edit ${title.replace(/s$/, '')}` : `New ${title.replace(/s$/, '')}`} onClose={() => setEditing(null)} wide>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.type === 'textarea' || f.type === 'image' || f.full ? 'sm:col-span-2' : ''}>
                {renderField(f, form, setForm, { products })}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-6 pt-4 border-t hairline">
            <button data-testid="resource-save" onClick={save} disabled={saving}
              className="bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(null)} className="border hairline px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest">Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
